import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { Button, Forbidden, List, Page, SearchForm } from 'shared/components';
import { displayValue, formatDate, hasPermission } from 'shared/helpers';
import { fetchLicenseUserGroups } from 'licenseUsers/actions';
import './styles.scss';

const PAGE_SIZE = 20;

const LicenseUserGroups = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const permissionsList = useSelector((state) =>
    get(state, 'user.details.permissions'),
  );
  const activeAccountId = useSelector((state) =>
    get(state, 'account.activeAccountId'),
  );
  const canManageLicenseUsers = hasPermission(
    permissionsList,
    'manage_license_users',
  );

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [retryToken, setRetryToken] = useState(0);

  const [activeQuery, setActiveQuery] = useState('');
  const [searchBy, setSearchBy] = useState('search');
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const emptyMessage = t('No license user groups to display.');

  useEffect(() => {
    setPage(0);
  }, [activeAccountId]);

  useEffect(() => {
    if (!canManageLicenseUsers) {
      return;
    }
    setLoading(true);
    setError(false);

    const filterPayload = {
      ...(activeAccountId ? { customer_account: activeAccountId } : {}),
    };

    fetchLicenseUserGroups({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      query: activeQuery,
      queryType: searchBy,
      orderBy: currentSort,
      filters: filterPayload,
    })
      .then((res) => {
        const payload = get(res, 'data');
        setGroups(get(payload, 'results', []));
        setCount(get(payload, 'count', 0));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setGroups([]);
        setCount(0);
        setLoading(false);
      });
  }, [
    canManageLicenseUsers,
    activeAccountId,
    activeQuery,
    currentSort,
    page,
    retryToken,
    searchBy,
  ]);

  const pages = Math.ceil(count / PAGE_SIZE);

  const searchOptions = useMemo(
    () => [
      {
        label: t('Search by all fields'),
        value: 'search',
      },
      {
        label: t('Search by code'),
        value: 'code__icontains',
      },
      {
        label: t('Search by name'),
        value: 'name__icontains',
      },
      {
        label: t('Search by customer account name'),
        value: 'customer_account__name__icontains',
      },
      {
        label: t('Search by customer account code'),
        value: 'customer_account__code__icontains',
      },
    ],
    [t],
  );

  const handleSearchSubmit = (val) => {
    setActiveQuery(val);
    setPage(0);
  };

  const handleClear = () => {
    setActiveQuery('');
    setPage(0);
  };

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleRowClick = (rowData) => {
    const groupId = get(rowData, 'original.id');
    if (groupId) {
      history.push(`/license_users/user_groups/${groupId}`);
    }
  };

  if (!canManageLicenseUsers) {
    return (
      <Page>
        <Forbidden
          text={t('Missing permission')}
          description={t('You do not have permission to perform this action.')}
        />
      </Page>
    );
  }

  return (
    <Page
      title={t('License user groups')}
      breadcrumb={<NavLink to="/license_users">{t('License users')}</NavLink>}
    >
      <div className="LicenseUserGroupsList">
        <div className="list-header">
          <SearchForm
            options={searchOptions}
            selectedValue={searchBy}
            handleSearchTypeChange={(newSearchType) =>
              setSearchBy(newSearchType)
            }
            handleSearchSubmit={handleSearchSubmit}
            activeQuery={activeQuery}
            handleClear={handleClear}
            disabled={loading}
          />
          <div />
        </div>
        {error && (
          <div className="LicenseUserGroupsList-error">
            <p>{t('Unable to load license user groups')}</p>
            <Button theme="info" size="sm" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        )}
        <List
          data={groups || []}
          noDataText={emptyMessage}
          clickable
          handleClick={handleRowClick}
          columns={[
            {
              accessor: 'created_at',
              Header: t('Created on'),
              Cell: (cellInfo) => formatDate(cellInfo.value),
              width: 160,
            },
            {
              accessor: 'name',
              Header: t('Name'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'code',
              Header: t('Code'),
              Cell: (cellData) => displayValue(cellData.value),
              width: 150,
            },
            {
              accessor: 'description',
              Header: t('Description'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              id: 'customer_account',
              Header: t('Customer account'),
              accessor: (row) => get(row, 'customer_account'),
              Cell: (cellData) => displayValue(cellData.value),
              width: 200,
            },
            {
              id: 'metadata',
              Header: t('Metadata'),
              accessor: (row) => get(row, 'metadata'),
              Cell: (cellData) => {
                const value = cellData.value;
                if (!value || Object.keys(value).length === 0) {
                  return displayValue(null, '{}');
                }
                return displayValue(JSON.stringify(value));
              },
              maxWidth: 320,
            },
          ]}
          page={page}
          pages={pages}
          loading={loading}
          manual
          minRows={groups.length || 10}
          showPagination={pages > 1}
          onPageChange={(newPage) => setPage(newPage)}
          onSortedChange={(newSorted) => setCurrentSort(newSorted)}
          defaultSorted={currentSort}
        />
      </div>
    </Page>
  );
};

export default LicenseUserGroups;
